import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { BaseEntity, BulkActionConfig } from './types';
import { UI_STRINGS } from './locale';

interface BulkActionsBarProps<TRecord extends BaseEntity> {
  selectedCount: number;
  selectedRows: TRecord[];
  actions: BulkActionConfig<TRecord>[];
  onClearSelection: () => void;
  className?: string;
}

/**
 * Плавающая панель действий над выбранными строками.
 * Отображается внизу экрана когда выбрана хотя бы одна строка.
 */
export function BulkActionsBar<TRecord extends BaseEntity>({
  selectedCount,
  selectedRows,
  actions,
  onClearSelection,
  className,
}: BulkActionsBarProps<TRecord>) {
  if (selectedCount === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed bottom-6 left-1/2 -translate-x-1/2 z-50',
        'flex items-center gap-3 px-4 py-3 rounded-xl',
        'bg-background border border-border shadow-xl',
        'animate-in slide-in-from-bottom-3 duration-200',
        className,
      )}
    >
      {/* Счётчик и кнопка сброса */}
      <div className="flex items-center gap-2 pr-3 border-r border-border">
        <span className="text-sm font-medium text-foreground">
          {UI_STRINGS.selected(selectedCount)}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
          onClick={onClearSelection}
          title={UI_STRINGS.clearSelection}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Кнопки действий */}
      <div className="flex items-center gap-2">
        {actions.map((action) => (
          <Button
            key={action.key}
            variant={action.variant ?? 'outline'}
            size="sm"
            className="h-8 gap-1.5"
            onClick={() => { void action.onClick(selectedRows); }}
          >
            {action.icon}
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
